import FileBlock from './base/file-block';
import fetchVideo from '@intercom/embercom-composer/lib/fetch-video';
import videoMetadata from '@intercom/embercom-composer/lib/video-metadata';
import openingTag from './../../lib/computed-properties/ic-block-opening-tag';
import { computed } from '@ember/object';
import { applyFunction, fmt, htmlEscape, ternaryToProperty, ternary } from '@intercom/pulse/lib/computed-properties';
import dataURItoBlob from '@intercom/embercom-composer/lib/data-uri-to-blob';
import VideoFileUrlParser from '@intercom/embercom-composer/lib/video-file-url-parser';
import { registerWaiter } from '@ember/test';
import $ from 'jquery';
import { equal, not, notEmpty } from '@ember/object/computed';
import Em from 'ember';
function splitCaptions(captions) {
  captions = captions || '';
  return captions.split(/\s*[\r\n]+\s*/);
}
export default FileBlock.extend({
  isMedia: true,
  isInline: true,
  isVideoFile: true,
  thumbnailUrl: null,
  type: 'videoFile',
  fetchFile(url) {
    url = VideoFileUrlParser.replaceUploadsDomain(url);
    let thumbnail = this.uploadThumbnail();
    let video = fetchVideo(url).then(duration => this.set('duration', duration));
    return Em.RSVP.all([thumbnail, video]);
  },
  hasEditor: not('hasUploadError'),
  hasCaptions: notEmpty('captions'),
  escapedUrl: htmlEscape('url'),
  escapedThumbnailUrl: htmlEscape('thumbnailUrl'),
  escapedInitialCaptions: htmlEscape('initialCaptions'),
  captions: '',
  duration: 0,
  captionsArray: applyFunction(splitCaptions, 'captions'),
  initialCaptions: Em.computed('captionsArray', function () {
    return this.get('captionsArray')[0];
  }),
  serialized: Em.computed('url', 'thumbnailUrl', 'type', 'captions', 'isUploadingOrHasUploadError', function () {
    if (this.get('isUploadingOrHasUploadError')) {
      return null;
    }
    return {
      url: this.get('url'),
      thumbnailUrl: this.get('thumbnailUrl'),
      type: this.get('type'),
      captions: this.get('captions'),
      duration: this.get('duration')
    };
  }),
  preprocess() {
    this.set('isUploading', true);
    return this._super().then(this.setMetadata.bind(this)).catch(this.uploadFail.bind(this));
  },
  setMetadata() {
    return new Em.RSVP.Promise((resolve, reject) => {
      videoMetadata(this.get('file')).then(metadata => {
        this.setProperties(metadata);
        resolve();
      }).catch(err => {
        reject(err);
      });
    });
  },
  uploadThumbnail() {
    if (this.get('thumbnailDataUrl')) {
      let blob = dataURItoBlob(this.get('thumbnailDataUrl'));
      let fileUpload = this.get('uploadManager').createFileUpload(blob, () => {});
      return fileUpload.upload().then(policy => {
        let thumbnailUrl = VideoFileUrlParser.replaceUploadsDomain(policy.public_url);
        this.set('thumbnailUrl', thumbnailUrl);
      });
    }
  },
  uploadSuccess(policy) {
    this.setProperties({
      isUploading: false,
      hasUploadError: false,
      url: VideoFileUrlParser.replaceUploadsDomain(policy.public_url),
      uploadId: policy.id
    });
  },
  uploadProgress(percentageComplete) {
    this._super(percentageComplete);
    this.get('uploadManager').doRender();
  },
  uploadFail(error) {
    this._super(error);
    this.get('uploadManager').doRender();
  },
  videoPlayed() {
    let video = this.getVideoElement();
    let controlClass = this.showAudioControl ? 'audio' : 'play';
    let controls = video.siblings(`.block-video-file-${controlClass}-control`);
    controls.removeClass('paused');
    video.on('timeupdate', () => {
      this.renderCaptions(video);
    });
    video.on('ended', () => {
      controls.addClass('replay');
      this.resetVideo(video, controls);
    });
    controls.removeClass('replay');
    this.set('isVideoLoading', true);
    let promise = video.get(0).play();
    // @pat This waiter is required to work around a "DOMException: The play() request was interrupted" error in tests on Chrome.
    // See the Chrome developer docs for more details https://goo.gl/LdLk22
    if (Ember.testing) {
      promise.finally(() => this.set('isVideoLoading', false));
      registerWaiter(() => !this.get('isVideoLoading'));
    }
  },
  captionsUpdated() {
    let video = this.getVideoElement();
    let controls = video.siblings('.block-video-file-play-control');
    this.resetVideo(video, controls);
  },
  resetVideo(video, playControls) {
    let videoElement = video.get(0);
    videoElement.pause();
    videoElement.currentTime = 0;
    playControls.addClass('paused');
    this.renderCaptions(video);
  },
  renderCaptions(video) {
    if (video.length) {
      let captionsElement = video.siblings('.block-video-file-captions')[0];
      let currentCaptions = this.getCurrentCaptions(video.prop('currentTime'), this.get('duration'));
      captionsElement.innerText = currentCaptions;
    }
  },
  getCurrentCaptions(currentTime, duration) {
    let captions = this.get('captionsArray');
    let captionDuration = duration / captions.length;
    let idx = Math.floor(currentTime / captionDuration);
    return captions[Math.min(idx, captions.length - 1)];
  },
  getVideoElement() {
    return $(`[data-id="${this.get('id')}"]`).find('video');
  },
  showAudioControl: equal('config.controlType', 'audio'),
  autoplayAttribute: Em.computed('config.videoFileAutoplay', function () {
    if (this.config.videoFileAutoplay) {
      return 'autoplay';
    }
    return '';
  }),
  mutedAttribute: Em.computed('config.videoFileMuted', function () {
    if (this.config.videoFileMuted) {
      return 'muted';
    }
    return '';
  }),
  initialPlayState: ternary('config.videoFileAutoplay', 'playing', 'paused'),
  openingTag: openingTag('url', 'id', 'hasUploadError', 'isUploading', 'hasNoFile'),
  closingTag: '</ic-block>',
  innerHtml: Em.computed('_zeroWidthSpace', '_paragraphInserter', 'escapedUrl', 'uploadStatusHtml', function () {
    return `${this.get('_zeroWidthSpace')}${this.get('_paragraphInserter')}
      <span contenteditable="false" class="block-video-file block-video-file-container ${this.get('uploadStatusClass')}">
        ${this.get('videoPlayerHtmlOrUploadStatusHtml')}
      </span>
      ${this.get('_paragraphInserter')}${this.get('_zeroWidthSpace')}`;
  }),
  uploadStatusClass: ternary('escapedUrl', 'block-video-file-container-uploaded', 'block-video-file'),
  videoPlayerHtmlOrUploadStatusHtml: ternaryToProperty('escapedUrl', 'videoPlayerHtml', 'uploadStatusHtml'),
  videoPlayerHtml: computed('videoElementHtml', 'captionsElementHtml', 'controlElementsHtml', function () {
    return `${this.videoElementHtml}${this.captionsElementHtml}${this.controlElementsHtml}`;
  }),
  videoElementHtml: computed('mutedAttribute', 'autoplayAttribute', 'escapedThumbnailUrl', 'escapedUrl', function () {
    return `<video class="block-video-file-player" ${this.mutedAttribute} ${this.autoplayAttribute} poster="${this.escapedThumbnailUrl}"><source src="${this.escapedUrl}"/></video>`;
  }),
  captionsElementHtml: computed('escapedInitialCaptions', function () {
    return `<div class="block-video-file-captions">${this.escapedInitialCaptions}</div>`;
  }),
  controlElementsHtml: ternaryToProperty('showAudioControl', 'audioControls', 'playPauseControls'),
  playPauseControls: computed('initialPlayState', function () {
    return `<div class="block-video-file-control block-video-file-play-control ${this.initialPlayState}"></div>`;
  }),
  audioControls: computed('mutedAttribute', function () {
    return `<div class="block-overlay"></div><div class="block-video-file-control block-video-file-audio-control ${this.mutedAttribute}"></div>`;
  }),
  uploadProgressHtml: computed('uploadProgressContent', function () {
    return `<div contenteditable="false" class="composer-file-uploadstatus"><div class="composer-file-uploadstatus-inner">${this.uploadProgressContent}</div></div>`;
  }),
  uploadProgressContent: '<div class="composer-file-uploadstatus-loading-indicator loading__indicator loading__indicator__graphic o__small u__mb__10"></div><div>Uploading video clip...</div>',
  uploadErrorHtml: fmt('<div contenteditable="false" class="composer-file-uploadstatus">' + '<div class="composer-file-uploadstatus-inner">' + '<div class="composer-file-error"><i class="composer-file-alert i o__24 o__alert"></i><span class="composer-file-error-message">Your video couldn\'t be uploaded</span></div>' + '</div>' + '</div>', 'id'),
  uploadTooLargeHtml: fmt(`<div contenteditable="false" class="composer-file-uploadstatus"><div class="composer-file-uploadstatus-inner">
     <span class="composer-file-error"><i class="composer-file-alert i o__24 o__alert"></i><span class="composer-file-error-message">%@</span></span>
     </div></div>`, 'errorMessage')
});